import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert, Box, Button, Container, InputAdornment,
  Link as MuiLink, Skeleton, TextField, Tooltip, Typography, alpha,
} from '@mui/material'
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid'
import { IconFileText, IconRefresh, IconSearch, IconVideo, IconWorldWww } from '@tabler/icons-react'
import { tokens } from '@/theme'
import { apiUrl } from '@/lib/apiBase'

interface GsaSummary {
  gsa_id: number
  name: string
  name_slug: string
  county: string | null
  dwr_region: string | null
  legal_entity_type: string | null
  website: string | null
  basin_name: string | null
  basin_number: string | null
  basin_priority_level: string | null
  basin_critically_overdrafted: boolean | null
  meeting_count: number
  agenda_count: number
  minutes_count: number
  last_meeting_date: string | null
  last_agenda_url: string | null
  last_minutes_url: string | null
  last_video_url: string | null
}

function RecordsCell({ row }: { row: GsaSummary }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', height: '100%', flexWrap: 'wrap' }}>
      {row.last_agenda_url && (
        <Tooltip title="Latest agenda document">
          <MuiLink
            href={row.last_agenda_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.4,
              fontSize: '0.75rem', fontFamily: '"DM Mono", monospace',
              color: tokens.green.dark, textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <IconFileText size={13} /> Agenda
          </MuiLink>
        </Tooltip>
      )}
      {row.last_minutes_url && (
        <Tooltip title="Latest meeting minutes">
          <MuiLink
            href={row.last_minutes_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.4,
              fontSize: '0.75rem', fontFamily: '"DM Mono", monospace',
              color: tokens.blue.main, textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <IconFileText size={13} /> Minutes
          </MuiLink>
        </Tooltip>
      )}
      {row.last_video_url && (
        <Tooltip title="Board meeting video recording">
          <MuiLink
            href={row.last_video_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.4,
              fontSize: '0.75rem', fontFamily: '"DM Mono", monospace',
              color: '#C4501A', textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <IconVideo size={13} /> Video
          </MuiLink>
        </Tooltip>
      )}
      {row.website && (
        <Tooltip title="GSA official website">
          <MuiLink
            href={row.website}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.4,
              fontSize: '0.75rem', fontFamily: '"DM Mono", monospace',
              color: alpha(tokens.green.dark, 0.7), textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <IconWorldWww size={13} /> Site
          </MuiLink>
        </Tooltip>
      )}
    </Box>
  )
}

function BasinCell({ row }: { row: GsaSummary }) {
  if (!row.basin_name) return <>—</>
  const detail = [
    row.basin_number && `Bulletin 118: ${row.basin_number}`,
    row.basin_priority_level && `Priority: ${row.basin_priority_level}`,
    row.basin_critically_overdrafted && 'Critically overdrafted',
  ].filter(Boolean).join(' · ')
  return (
    <Tooltip title={detail || row.basin_name}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, height: '100%', minWidth: 0 }}>
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit', overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: row.basin_critically_overdrafted ? '#C4501A' : 'inherit',
            fontWeight: row.basin_critically_overdrafted ? 600 : 400,
          }}
        >
          {row.basin_name}
        </Typography>
        {row.basin_priority_level && (
          <Typography
            component="span"
            sx={{
              fontSize: '0.68rem', fontFamily: '"DM Mono", monospace',
              color: alpha(tokens.green.dark, 0.6), flexShrink: 0,
            }}
          >
            {row.basin_priority_level}
          </Typography>
        )}
      </Box>
    </Tooltip>
  )
}

const COLUMNS: GridColDef<GsaSummary>[] = [
  { field: 'name', headerName: 'GSA Name', flex: 2, minWidth: 220 },
  {
    field: 'basin_name', headerName: 'Basin', flex: 1.4, minWidth: 180,
    renderCell: (p: GridRenderCellParams<GsaSummary>) => <BasinCell row={p.row} />,
  },
  {
    field: 'county', headerName: 'County', flex: 1, minWidth: 130,
    valueGetter: (v: string | null) => v ?? '—',
  },
  {
    field: 'dwr_region', headerName: 'DWR Region', flex: 1, minWidth: 140,
    valueGetter: (v: string | null) => v ?? '—',
  },
  { field: 'meeting_count', headerName: 'Meetings', width: 100, type: 'number' },
  { field: 'agenda_count', headerName: 'Agendas', width: 95, type: 'number' },
  { field: 'minutes_count', headerName: 'Minutes', width: 95, type: 'number' },
  {
    field: 'links',
    headerName: 'Public Records',
    flex: 1.5,
    minWidth: 200,
    sortable: false,
    renderCell: (p: GridRenderCellParams<GsaSummary>) => <RecordsCell row={p.row} />,
  },
]

export default function GsaData() {
  const navigate = useNavigate()
  const [gsas, setGsas] = useState<GsaSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    fetch(apiUrl('/api/agro/gsas/summary'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data: GsaSummary[]) => setGsas(data))
      .catch(() => setError('Failed to load GSA data. Please try again.'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = useMemo(() => {
    if (!search.trim()) return gsas
    const q = search.toLowerCase()
    return gsas.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.basin_name ?? '').toLowerCase().includes(q) ||
        (g.basin_number ?? '').toLowerCase().includes(q) ||
        (g.county ?? '').toLowerCase().includes(q) ||
        (g.dwr_region ?? '').toLowerCase().includes(q),
    )
  }, [gsas, search])

  const basinCount = new Set(gsas.map((g) => g.basin_name).filter(Boolean)).size
  const totalMeetings = gsas.reduce((s, g) => s + g.meeting_count, 0)
  const totalAgendas = gsas.reduce((s, g) => s + g.agenda_count, 0)
  const totalMinutes = gsas.reduce((s, g) => s + g.minutes_count, 0)

  return (
    <Box sx={{ background: tokens.cream, minHeight: '100vh' }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${tokens.green.dark} 0%, ${tokens.green.main} 100%)`,
          color: '#fff',
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="overline"
            sx={{ color: alpha('#fff', 0.6), display: 'block', mb: 1, letterSpacing: '0.15em' }}
          >
            Public Data
          </Typography>
          <Typography
            variant="h1"
            sx={{

              fontSize: { xs: '2.2rem', md: '3rem' },
              fontWeight: 700,
              mb: 2,
              lineHeight: 1.15,
            }}
          >
            California GSA Meeting Records
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: alpha('#fff', 0.8), maxWidth: 640, fontSize: '1.05rem', mb: 4 }}
          >
            California's Groundwater Sustainability Agencies (GSAs) manage local basins under SGMA.
            This page tracks their public board meeting records — agendas, minutes, and official
            website links where video recordings are often posted.
          </Typography>

          {/* Summary stats */}
          {!loading && !error && (
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {[
                { label: 'Active GSAs', value: gsas.length },
                { label: 'Basins Covered', value: basinCount },
                { label: 'Board Meetings', value: totalMeetings.toLocaleString() },
                { label: 'Agenda Docs', value: totalAgendas.toLocaleString() },
                { label: 'Minutes Docs', value: totalMinutes.toLocaleString() },
              ].map(({ label, value }) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: '2rem', fontWeight: 700, lineHeight: 1 }}>
                    {value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: alpha('#fff', 0.6), mt: 0.25 }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Container>
      </Box>

      {/* Table section */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        {/* Search */}
        <Box sx={{ mb: 3 }}>
          <TextField
            size="small"
            placeholder="Search by GSA name, basin, county, or DWR region…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconSearch size={16} color={tokens.green.main} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              width: { xs: '100%', sm: 380 },
              '& .MuiOutlinedInput-root': {
                background: '#fff',
                borderRadius: 2,
                '&:hover fieldset': { borderColor: tokens.green.main },
                '&.Mui-focused fieldset': { borderColor: tokens.green.main },
              },
            }}
          />
        </Box>

        {/* Loading */}
        {loading && (
          <Box>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={52} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        )}

        {/* Error */}
        {!loading && error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" startIcon={<IconRefresh size={16} />} onClick={load}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
            <Typography>
              {search ? `No GSAs match "${search}"` : 'No GSA data available yet.'}
            </Typography>
          </Box>
        )}

        {/* Data grid */}
        {!loading && !error && filtered.length > 0 && (
          <DataGrid
            rows={filtered}
            columns={COLUMNS}
            getRowId={(row) => row.gsa_id}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[25, 50, 100]}
            onRowClick={(params) => navigate(`/gsa-data/${params.row.name_slug}`)}
            autoHeight
            rowHeight={56}
            sx={{
              background: '#fff',
              borderRadius: 2,
              border: `1px solid ${alpha(tokens.green.main, 0.15)}`,
              '& .MuiDataGrid-columnHeader': {
                background: alpha(tokens.green.main, 0.06),
                fontFamily: '"Syne", sans-serif',
                fontWeight: 700,
                fontSize: '0.78rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              },
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-row:hover': {
                background: alpha(tokens.green.main, 0.04),
              },
            }}
          />
        )}

        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
          Data sourced from public SGMA registry and GSA board meeting portals. Meeting records are
          updated continuously by the Agrolense pipeline.
        </Typography>
      </Container>
    </Box>
  )
}
